const API_BASE = `${process.env.API_BASE_URL}/api/addin`;
const FRONT_BASE_URL = process.env.FRONT_BASE_URL;

const LINK_ENDPOINT =
  `${API_BASE}/link-email`;

const EMAIL_TREE_ENDPOINT =
  `${API_BASE}/email-link-tree`;

const COMPANY_TREE_ENDPOINT =
  `${API_BASE}/company`;

const COMPANY_SEARCH_ENDPOINT =
  `${API_BASE}/company/search`;

const CREATE_PERSON_ENDPOINT =
  `${API_BASE}/create-person`;

const COMMERCIAL_PROPOSAL_LIMIT = 5;

const SEARCH_DEBOUNCE_MS = 300;

const SEARCH_MIN_LENGTH = 2;

const TOKEN_REFRESH_BUFFER_MS =
  5 * 60 * 1000;

/*
 * Minimum time between two accepted clicks
 * on a given action button.
 */
const ACTION_CLICK_LOCK_MS = 800;


/* ================================================================== */
/* State                                                              */
/* ================================================================== */

let currentItem = null;

let tokenPromise = null;

let cachedToken = null;

let cachedTokenExpiry = 0;

let isSubmitting = false;

let isCreatingPerson = false;

let initialized = false;

let searchDebounceTimer = null;

/*
 * Company selected through the company-search workflow.
 *
 * This is the company that will be attached to a
 * newly created person.
 */
let selectedCompany = null;


/* ================================================================== */
/* Empty states                                                       */
/* ================================================================== */

const EMPTY_STATE_NO_SENDER_MATCH =
  "Utilisez l’onglet Rechercher pour trouver une entreprise.";

const EMPTY_STATE_NO_COMPANY_ITEMS =
  "Aucune offre ou proposition commerciale trouvée pour cette entreprise.";


const els = {};


/* ================================================================== */
/* Office initialization                                              */
/* ================================================================== */

Office.onReady((info) => {

  if (info.host === Office.HostType.Outlook) {
    init();
  }

});


function init() {

  if (initialized) {
    return;
  }

  console.log(
    "Initializing add-in..."
  );

  currentItem =
    Office.context.mailbox.item;


  const ids = [

    "previewSubject",
    "previewFrom",

    "suggestionsLoading",
    "suggestionsEmpty",
    "suggestionsEmptyMessage",
    "suggestionsError",

    "peopleTree",

    "createPerson",
    "createPersonEmail",
    "createPersonName",
    "createPersonCivility",
    "createPersonPhone",
    "createPersonBtn",
    "createPersonStatus",

    "selectedCompany",
    "selectedCompanyName",
    "selectedCompanyId",

    "companySearchInput",
    "companySearchSpinner",
    "companyResults",

    "companyPeopleTree",

    "targetType",
    "targetId",

    "selectedTarget",
    "selectedTargetValue",
    "clearSelectedTarget",

    "confirmBtn",
    "status",

    "tabSuggestions",
    "tabSearch",
    "tabManual",

    "panelSuggestions",
    "panelSearch",
    "panelManual"

  ];


  const missing = [];


  for (const id of ids) {

    const el =
      document.getElementById(id);

    if (!el) {
      missing.push(id);
    } else {
      els[id] = el;
    }

  }


  if (missing.length) {

    console.error(
      "Missing DOM elements:",
      missing
    );

    return;
  }


  initialized = true;


  /* -------------------------------------------------------------- */
  /* Mail preview                                                    */
  /* -------------------------------------------------------------- */

  els.previewSubject.textContent =
    currentItem.subject ||
    "(sans objet)";


  els.previewFrom.textContent =
    currentItem.from
      ? `de ${
          currentItem.from.displayName ||
          currentItem.from.emailAddress
        }`
      : "expéditeur inconnu";


  /* -------------------------------------------------------------- */
  /* Confirm                                                         */
  /* -------------------------------------------------------------- */

  els.confirmBtn.addEventListener(
    "click",
    () => {

      if (
        !requestActionLock(
          els.confirmBtn
        )
      ) {
        return;
      }

      onConfirm(
        els.targetType.value,
        els.targetId.value.trim()
      );

    }
  );


  els.targetType.addEventListener(
    "change",
    syncSelectedTargetFromManualFields
  );


  els.targetId.addEventListener(
    "input",
    syncSelectedTargetFromManualFields
  );


  els.clearSelectedTarget.addEventListener(
    "click",
    clearSelectedTarget
  );


  /* -------------------------------------------------------------- */
  /* Company search                                                  */
  /* -------------------------------------------------------------- */

  els.companySearchInput.addEventListener(
    "input",
    onCompanySearchInput
  );


  /* -------------------------------------------------------------- */
  /* Create contact                                                   */
  /* -------------------------------------------------------------- */

  els.createPersonEmail.value =
    currentItem.from
      ? currentItem.from.emailAddress || ""
      : "";


  els.createPersonName.value =
    currentItem.from
      ? currentItem.from.displayName || ""
      : "";


  els.createPersonBtn.addEventListener(
    "click",
    () => {

      if (
        !requestActionLock(
          els.createPersonBtn
        )
      ) {
        return;
      }

      onCreatePerson();

    }
  );


  /* -------------------------------------------------------------- */
  /* Initial state                                                    */
  /* -------------------------------------------------------------- */

  setSelectedCompany(null);

  switchTab("suggestions");

  loadSuggestionsForSender();

}


/* ================================================================== */
/* Tab click handling                                                 */
/* ================================================================== */

document.addEventListener(
  "click",
  (event) => {

    const tab =
      event.target.closest(".tab");

    if (!tab) {
      return;
    }


    switch (tab.id) {

      case "tabSuggestions":

        switchTab("suggestions");

        break;


      case "tabSearch":

        switchTab("search");

        break;


      case "tabManual":

        switchTab("manual");

        break;

    }

  }
);


/* ================================================================== */
/* Duplicate-click guard                                              */
/* ================================================================== */

function requestActionLock(buttonEl) {

  if (buttonEl.disabled) {
    return false;
  }


  const now =
    Date.now();


  const lastClick =
    Number(
      buttonEl.dataset.lastClickAt || 0
    );


  if (
    now - lastClick <
    ACTION_CLICK_LOCK_MS
  ) {
    return false;
  }


  buttonEl.dataset.lastClickAt =
    String(now);


  return true;

}


/* ================================================================== */
/* Generic collapsible tree sections                                  */
/* ================================================================== */

function buildTreeSection(
  type,
  title,
  subtitle,
  items,
  cardBuilder
) {

  const section =
    document.createElement("section");

  section.className =
    "tree-section";

  section.dataset.type =
    type;


  const header =
    document.createElement("div");

  header.className =
    "tree-section__header";

  header.role =
    "button";

  header.tabIndex =
    0;

  header.setAttribute(
    "aria-expanded",
    "false"
  );


  const content =
    document.createElement("div");


  const titleRow =
    document.createElement("div");

  titleRow.style.display =
    "flex";

  titleRow.style.gap =
    "3px";

  titleRow.style.alignItems =
    "center";


  const heading =
    document.createElement("h2");

  heading.textContent =
    title;


  const count =
    document.createElement("span");

  count.className =
    "tree-section__count";

  count.textContent =
    String(items.length);


  titleRow.append(
    heading,
    count
  );


  const description =
    document.createElement("span");

  description.textContent =
    subtitle;


  content.append(
    titleRow,
    description
  );


  const chevron =
    document.createElement("span");

  chevron.className =
    "tree-section__chevron";

  chevron.setAttribute(
    "aria-hidden",
    "true"
  );

  chevron.textContent =
    "▼";


  header.append(
    content,
    chevron
  );


  const list =
    document.createElement("div");

  list.className =
    "card-list";

  list.hidden =
    true;

  list.style.display =
    "none";


  for (const item of items) {

    list.appendChild(
      cardBuilder(item)
    );

  }


  setTreeSectionExpanded(
    header,
    list,
    chevron,
    false
  );


  header.addEventListener(
    "click",
    (event) => {

      if (
        event.target.closest(
          "a, button, input, select"
        )
      ) {
        return;
      }


      const expanded =
        header.getAttribute(
          "aria-expanded"
        ) === "true";


      setTreeSectionExpanded(
        header,
        list,
        chevron,
        !expanded
      );

    }
  );


  header.addEventListener(
    "keydown",
    (event) => {

      if (
        event.key !== "Enter" &&
        event.key !== " "
      ) {
        return;
      }


      event.preventDefault();


      const expanded =
        header.getAttribute(
          "aria-expanded"
        ) === "true";


      setTreeSectionExpanded(
        header,
        list,
        chevron,
        !expanded
      );

    }
  );


  section.append(
    header,
    list
  );


  return section;

}


function setTreeSectionExpanded(
  headerEl,
  listEl,
  chevronEl,
  expanded
) {

  headerEl.setAttribute(
    "aria-expanded",
    expanded
      ? "true"
      : "false"
  );


  listEl.hidden =
    !expanded;


  listEl.style.display =
    expanded
      ? ""
      : "none";


  if (chevronEl) {

    chevronEl.style.transform =
      expanded
        ? "rotate(0deg)"
        : "rotate(-90deg)";

  }

}


/* ================================================================== */
/* Tabs                                                               */
/* ================================================================== */

function switchTab(tabName) {

  const tabs = {

    suggestions: {
      button:
        document.getElementById(
          "tabSuggestions"
        ),

      panel:
        document.getElementById(
          "panelSuggestions"
        )
    },


    search: {
      button:
        document.getElementById(
          "tabSearch"
        ),

      panel:
        document.getElementById(
          "panelSearch"
        )
    },


    manual: {
      button:
        document.getElementById(
          "tabManual"
        ),

      panel:
        document.getElementById(
          "panelManual"
        )
    }

  };


  const selected =
    tabs[tabName];


  if (!selected) {

    console.error(
      "Unknown tab:",
      tabName
    );

    return;
  }


  Object.entries(tabs)
    .forEach(
      ([name, tab]) => {

        const active =
          name === tabName;


        tab.button.classList.toggle(
          "tab--active",
          active
        );


        tab.button.setAttribute(
          "aria-selected",
          active
            ? "true"
            : "false"
        );


        tab.panel.hidden =
          !active;

      }
    );


  if (tabName === "manual") {

    syncSelectedTargetFromManualFields();

  }

}


/* ================================================================== */
/* Email -> people -> company -> tree                                 */
/* ================================================================== */

async function loadSuggestionsForSender() {

  const senderEmail =
    currentItem.from
      ? currentItem.from.emailAddress
      : null;


  if (!senderEmail) {

    showSuggestionsEmpty();

    return;
  }


  try {

    const token =
      await getAccessToken();


    const url =
      `${EMAIL_TREE_ENDPOINT}` +
      `?email=${encodeURIComponent(
        senderEmail
      )}` +
      `&commercialProposalLimit=${
        COMMERCIAL_PROPOSAL_LIMIT
      }`;


    console.log(
      "Loading email tree:",
      url
    );


    const tree =
      await fetchJson(
        url,
        token
      );


    console.log(
      "Email tree response:",
      tree
    );


    renderTreeOutcome(tree);

  } catch (err) {

    console.error(
      "Failed to load sender's fiche tree:",
      err
    );


    showSuggestionsError();

  }

}


function renderTreeOutcome(tree) {

  const people =
    normalizePeopleTree(tree);


  els.suggestionsLoading.hidden =
    true;


  if (!people.length) {

    showSuggestionsEmpty();

    return;
  }

  els.suggestionsEmpty.hidden =
    true;

  els.suggestionsError.hidden =
    true;

  els.peopleTree.hidden =
    false;


  /*
   * Contact creation is no longer part of
   * the suggestions workflow.
   */
  setSelectedCompany(null);

  renderPeopleTree(people);

}


function renderPeopleTree(people) {

  els.peopleTree.innerHTML =
    "";


  people.forEach(
    (person, index) => {

      els.peopleTree.appendChild(
        buildPersonNode(
          person,
          index === 0
        )
      );

    }
  );

}


function buildPersonNode(
  person,
  initiallyExpanded = false
) {

  const section =
    document.createElement("section");

  section.className =
    "person-tree-node";


  /* -------------------------------------------------------------- */
  /* Person header                                                    */
  /* -------------------------------------------------------------- */

  const header =
    document.createElement("div");

  header.className =
    "person-tree-node__header";

  header.setAttribute(
    "role",
    "button"
  );

  header.setAttribute(
    "tabindex",
    "0"
  );

  header.setAttribute(
    "aria-expanded",
    initiallyExpanded
      ? "true"
      : "false"
  );


  const chevron =
    document.createElement("span");

  chevron.className =
    "person-tree-node__chevron";

  chevron.setAttribute(
    "aria-hidden",
    "true"
  );

  chevron.textContent =
    "▾";


  const icon =
    document.createElement("span");

  // icon.className =
  //   "person-tree-node__icon";

  // icon.setAttribute(
  //   "aria-hidden",
  //   "true"
  // );

  // icon.textContent =
  //   "";


  const info =
    document.createElement("div");

  info.className =
    "person-tree-node__info";


  const name =
    document.createElement("strong");

  name.className =
    "person-tree-node__name";

  name.textContent =
    person.name ||
    `Personne #${person.id}`;


  const id =
    document.createElement("span");

  id.className =
    "person-tree-node__id";

  id.textContent =
    `#${person.id}`;


  info.append(
    name,
    id
  );


  header.append(
    chevron,
    icon,
    info
  );


  /* -------------------------------------------------------------- */
  /* Companies                                                       */
  /* -------------------------------------------------------------- */

  const companies =
    document.createElement("div");

  companies.className =
    "person-tree-node__companies";


  companies.hidden =
    !initiallyExpanded;


  companies.style.display =
    initiallyExpanded
      ? ""
      : "none";


  if (!person.companies.length) {

    const empty =
      document.createElement("div");

    empty.className =
      "person-tree-node__empty";

    empty.textContent =
      "Aucune entreprise associée.";


    companies.appendChild(
      empty
    );

  } else {

    for (
      const company of person.companies
    ) {

      companies.appendChild(
        buildCompanyNode(company)
      );

    }

  }


  /* -------------------------------------------------------------- */
  /* Accordion behavior                                              */
  /* -------------------------------------------------------------- */

  const toggle = () => {

    const expanded =
      header.getAttribute(
        "aria-expanded"
      ) === "true";


    document
      .querySelectorAll(
        ".person-tree-node"
      )
      .forEach(
        (otherPerson) => {

          if (
            otherPerson === section
          ) {
            return;
          }


          const otherHeader =
            otherPerson.querySelector(
              ".person-tree-node__header"
            );


          const otherCompanies =
            otherPerson.querySelector(
              ".person-tree-node__companies"
            );


          const otherChevron =
            otherPerson.querySelector(
              ".person-tree-node__chevron"
            );


          if (
            !otherHeader ||
            !otherCompanies
          ) {
            return;
          }


          otherHeader.setAttribute(
            "aria-expanded",
            "false"
          );


          otherCompanies.hidden =
            true;


          otherCompanies.style.display =
            "none";


          if (otherChevron) {

            otherChevron.style.transform =
              "rotate(-90deg)";

          }

        }
      );


    const nextExpanded =
      !expanded;


    header.setAttribute(
      "aria-expanded",
      nextExpanded
        ? "true"
        : "false"
    );


    companies.hidden =
      !nextExpanded;


    companies.style.display =
      nextExpanded
        ? ""
        : "none";


    chevron.style.transform =
      nextExpanded
        ? "rotate(0deg)"
        : "rotate(-90deg)";

  };


  header.addEventListener(
    "click",
    toggle
  );


  header.addEventListener(
    "keydown",
    (event) => {

      if (
        event.key !== "Enter" &&
        event.key !== " "
      ) {
        return;
      }


      event.preventDefault();

      toggle();

    }
  );


  section.append(
    header,
    companies
  );


  chevron.style.transform =
    initiallyExpanded
      ? "rotate(0deg)"
      : "rotate(-90deg)";


  return section;

}


/* ================================================================== */
/* Company node                                                       */
/* ================================================================== */

function buildCompanyNode(company) {

  const section =
    document.createElement("section");

  section.className =
    "company-tree-node";


  /* -------------------------------------------------------------- */
  /* Company header                                                   */
  /* -------------------------------------------------------------- */

  const link =
    document.createElement("a");

  link.className =
    "company-header";


  link.href =
    `${FRONT_BASE_URL}/dashboard/companies/details/${
      encodeURIComponent(company.id)
    }`;


  link.target =
    "_blank";


  link.rel =
    "noopener noreferrer";
 
 const onOpen = () => logCompanyRedirection(company.id);
  link.addEventListener("click", onOpen);
  link.addEventListener("auxclick", (event) => {
    if (event.button === 1) onOpen();
  });


  link.setAttribute(
    "aria-label",
    "Ouvrir la fiche de l’entreprise dans Freyja"
  );


  const icon =
    document.createElement("span");

  icon.className =
    "company-header__icon";

  icon.setAttribute(
    "aria-hidden",
    "true"
  );

  icon.textContent =
    "🏢";


  const content =
    document.createElement("span");

  content.className =
    "company-header__content";


  const label =
    document.createElement("span");

  label.className =
    "company-header__label";

  label.textContent =
    "Entreprise";


  const name =
    document.createElement("strong");

  name.className =
    "company-header__name";

  name.textContent =
    company.name ||
    `Entreprise #${company.id}`;


  const id =
    document.createElement("span");

  id.className =
    "company-header__id";

  id.textContent =
    `#${company.id}`;


  content.append(
    label,
    name,
    id
  );


  link.append(
    icon,
    content
  );


  section.appendChild(
    link
  );


  /* -------------------------------------------------------------- */
  /* Offers                                                           */
  /* -------------------------------------------------------------- */

  if (company.offers.length) {

    section.appendChild(
      buildTreeSection(
        "offers",
        "Offres",
        "Offres associées",
        company.offers,
        buildOfferCard
      )
    );

  }


  /* -------------------------------------------------------------- */
  /* Commercial proposals                                             */
  /* -------------------------------------------------------------- */

  if (
    company.commercialProposals.length
  ) {

    section.appendChild(
      buildTreeSection(
        "commercial-proposals",
        "Propositions commerciales",
        "Propositions actives",
        company.commercialProposals,
        buildCommercialProposalCard
      )
    );

  }


  return section;

}


/* ================================================================== */
/* Email tree normalization                                           */
/* ================================================================== */

function normalizePeopleTree(tree) {

  if (
    !tree ||
    typeof tree !== "object"
  ) {
    return [];
  }


  const rootNodes =
    Array.isArray(tree.nodes)
      ? tree.nodes
      : [];


  const people = [];


  for (
    const personNode of rootNodes
  ) {

    if (
      !personNode ||
      personNode.type !== "person"
    ) {
      continue;
    }


    const companies = [];


    const companyNodes =
      Array.isArray(personNode.nodes)
        ? personNode.nodes
        : [];


    for (
      const companyNode of companyNodes
    ) {

      if (
        !companyNode ||
        companyNode.type !== "company"
      ) {
        continue;
      }


      let offers = [];

      let commercialProposals = [];


      const containers =
        Array.isArray(companyNode.nodes)
          ? companyNode.nodes
          : [];


      for (
        const container of containers
      ) {

        if (
          !container ||
          !Array.isArray(
            container.nodes
          )
        ) {
          continue;
        }


        if (
          container.type === "offers"
        ) {

          offers =
            container.nodes;

        }


        if (
          container.type ===
          "commercial_proposals"
        ) {

          commercialProposals =
            container.nodes;

        }

      }


      companies.push({

        id:
          companyNode.id,

        type:
          "company",

        name:
          companyNode.name,

        offers,

        commercialProposals

      });

    }


    people.push({

      id:
        personNode.id,

      type:
        "person",

      name:
        personNode.name,

      companies

    });

  }


  return people;

}


/* ================================================================== */
/* Company-tree normalization                                         */
/* ================================================================== */

function normalizeTree(tree) {

  if (
    !tree ||
    typeof tree !== "object"
  ) {
    return null;
  }


  const rootNodes =
    Array.isArray(tree.nodes)
      ? tree.nodes
      : [];


  let company =
    tree.company || null;


  const offers = [];

  const commercialProposals = [];


  const companyNode =
    rootNodes.find(
      (node) =>
        node?.type === "company"
    );


  if (
    !company &&
    companyNode
  ) {

    company =
      companyNode;

  }


  const containers = [];


  if (
    companyNode &&
    Array.isArray(
      companyNode.nodes
    )
  ) {

    containers.push(
      ...companyNode.nodes
    );

  }


  containers.push(
    ...rootNodes.filter(
      (node) =>
        node?.type === "offers" ||
        node?.type ===
          "commercial_proposals"
    )
  );


  for (
    const container of containers
  ) {

    if (
      !container ||
      !Array.isArray(
        container.nodes
      )
    ) {
      continue;
    }


    if (
      container.type === "offers"
    ) {

      offers.push(
        ...container.nodes
      );

    }


    if (
      container.type ===
      "commercial_proposals"
    ) {

      commercialProposals.push(
        ...container.nodes
      );

    }

  }


  return {

    email:
      tree.email || null,

    company,

    offers,

    commercialProposals

  };

}


/* ================================================================== */
/* Standalone company tree rendering                                  */
/* ================================================================== */

function renderTree(tree) {

  /*
   * Search workflow has its own tree container.
   *
   * This avoids mixing the selected company tree
   * with the sender suggestions tree.
   */

  els.companyPeopleTree.innerHTML =
    "";


  const company =
    tree.company || null;


  const companyNode = {

    id:
      company?.id,

    type:
      "company",

    name:
      company?.name ||
      "Entreprise",

    offers:
      tree.offers || [],

    commercialProposals:
      tree.commercialProposals || []

  };


  const wrapper =
    document.createElement("div");


  wrapper.className =
    "company-tree-node company-tree-node--standalone";


  wrapper.appendChild(
    buildCompanyNode(
      companyNode
    )
  );


  els.companyPeopleTree.appendChild(
    wrapper
  );


  els.companyPeopleTree.hidden =
    false;

}


/* ================================================================== */
/* Offers                                                             */
/* ================================================================== */

function buildOfferCard(offer) {

  const card =
    document.createElement("button");

  card.type =
    "button";

  card.className =
    "card-item";

  card.dataset.type =
    "offer";

  card.dataset.id =
    String(offer.id);


  const title =
    document.createElement("span");

  title.className =
    "card-item__title";


  title.textContent =
    offer.product
      ? `Offre #${offer.id} ${offer.product} `
      : `Offre #${offer.id}`;


  card.appendChild(
    title
  );


  if (offer.step_name) {

    const badge =
      document.createElement("span");


    badge.className =
      offer.step_name ===
      "6 - Démarré"

        ? "badge bg-label-primary"

        : "badge bg-label-warning";


    badge.textContent =
      offer.step_name;


    card.appendChild(
      badge
    );

  }


  const metaParts = [];


  const meta =
    createMetaElement(
      metaParts
    );


  if (meta) {

    card.appendChild(
      meta
    );

  }


  card.addEventListener(
    "click",
    () => {

      selectTarget(
        "offer",
        offer.id,
        offer.name ||
          `Offre #${offer.id}`,
        card
      );

    }
  );


  return card;

}


/* ================================================================== */
/* Commercial proposals                                               */
/* ================================================================== */

function buildCommercialProposalCard(
  proposal
) {

  const card =
    document.createElement("button");

  card.type =
    "button";

  card.className =
    "card-item";

  card.dataset.type =
    "commercial_proposal";

  card.dataset.id =
    String(proposal.id);


  const title =
    document.createElement("span");

  title.className =
    "card-item__title";


  title.textContent =
    proposal.name ||
    proposal.proposer_number ||
    `Proposition commerciale #${proposal.id}`;


  card.appendChild(
    title
  );


  const metaParts = [];


  if (proposal.product) {

    metaParts.push(
      proposal.product
    );

  }


  const meta =
    createMetaElement(
      metaParts
    );


  if (meta) {

    card.appendChild(
      meta
    );

  }


  if (
    proposal.relevanceLevel
  ) {

    let badgeClass = "";


    switch (
      proposal.relevanceLevel
    ) {

      case "Degré 0 - Mandat Signé":

        badgeClass =
          "bg-label-primary";

        break;


      case "Degré 1 - Forte pertinence":

        badgeClass =
          "bg-label-success";

        break;


      case "Degré 2 - Moyenne Pertinence":

        badgeClass =
          "bg-label-warning";

        break;


      case "Degré 3 - Faible Pertinence":

        badgeClass =
          "bg-label-danger";

        break;


      default:

        badgeClass =
          "bg-secondary";

    }


    const badge =
      document.createElement("span");


    badge.className =
      `badge ${badgeClass}`;


    badge.textContent =
      proposal.relevanceLevel;


    card.appendChild(
      badge
    );

  }


  card.addEventListener(
    "click",
    () => {

      selectTarget(
        "commercial_proposal",
        proposal.id,
        proposal.name ||
          proposal.proposer_number ||
          `Proposition commerciale #${proposal.id}`,
        card
      );

    }
  );


  return card;

}


function createMetaElement(parts) {

  if (!parts.length) {
    return null;
  }


  const meta =
    document.createElement("span");


  meta.className =
    "card-item__meta";


  meta.textContent =
    parts.join(" · ");


  return meta;

}

/* ================================================================== */
/* Suggestion states                                                  */
/* ================================================================== */

function showSuggestionsEmpty() {

  els.suggestionsLoading.hidden =
    true;


  els.suggestionsError.hidden =
    true;


  els.suggestionsEmptyMessage.textContent =
    EMPTY_STATE_NO_SENDER_MATCH;


  els.suggestionsEmpty.hidden =
    false;


  els.peopleTree.hidden =
    true;


  els.peopleTree.innerHTML =
    "";


  /*
   * IMPORTANT:
   *
   * No contact creation here.
   *
   * The user must first go to Rechercher,
   * select a company, and then create the person.
   */

  setSelectedCompany(null);

}


function showSuggestionsError() {

  els.suggestionsLoading.hidden =
    true;


  els.suggestionsEmpty.hidden =
    true;


  els.suggestionsError.hidden =
    false;


  els.peopleTree.hidden =
    true;


  els.peopleTree.innerHTML =
    "";


  /*
   * Do not display the create-person form
   * from the suggestions workflow.
   */

  setSelectedCompany(null);

}


/* ================================================================== */
/* Selected company                                                   */
/* ================================================================== */

function setSelectedCompany(company) {

  selectedCompany =
    company || null;


  if (!selectedCompany) {

    els.selectedCompany.hidden =
      true;


    els.selectedCompanyName.textContent =
      "";


    els.selectedCompanyId.textContent =
      "";


    setCreatePersonVisible(
      false
    );


    return;
  }


  els.selectedCompanyName.textContent =
    selectedCompany.name ||
    `Entreprise #${selectedCompany.id}`;


  els.selectedCompanyId.textContent =
    `#${selectedCompany.id}`;


  els.selectedCompany.hidden =
    false;


  /*
   * A company is now selected,
   * so contact creation becomes available.
   */

  setCreatePersonVisible(
    true
  );


  showCreatePersonStatus(
    "",
    ""
  );

}


/* ================================================================== */
/* Create contact                                                     */
/* ================================================================== */

function setCreatePersonVisible(
  visible
) {

  els.createPerson.hidden =
    !visible;


  if (!visible) {

    showCreatePersonStatus(
      "",
      ""
    );

  }

}


function showCreatePersonStatus(
  kind,
  message
) {

  els.createPersonStatus.className =
    "status" +
    (
      kind
        ? " " + kind
        : ""
    );


  els.createPersonStatus.textContent =
    message;

}


async function onCreatePerson() {

  if (isCreatingPerson) {
    return;
  }

  if (
    !selectedCompany ||
    !selectedCompany.id
  ) {

    showCreatePersonStatus(
      "error",
      "✖ Veuillez d’abord sélectionner une entreprise."
    );

    return;
  }


  const email =
    els.createPersonEmail.value.trim();


  const fullName =
    els.createPersonName.value.trim();

  const civilityId =
    els.createPersonCivility.value.trim();

  const phone =
    els.createPersonPhone.value.trim();


  if (
    !email ||
    !fullName
  ) {

    showCreatePersonStatus(
      "error",
      "✖ Veuillez renseigner l’email et le nom complet."
    );

    return;
  }


  const companyId =
    Number.parseInt(
      selectedCompany.id,
      10
    );


  if (
    !Number.isInteger(companyId)
  ) {

    showCreatePersonStatus(
      "error",
      "✖ L’identifiant de l’entreprise est invalide."
    );

    return;
  }


  /*
   * ================================================================
   * CREATE PERSON PAYLOAD
   * ================================================================
   *
   * This is the payload to send to your backend.
   */

  const payload = {

    email,

    fullName,

    companyId,

    phone, 

    civilityId

  };


  console.log(
    "Create person payload:",
    payload
  );


  isCreatingPerson =
    true;


  els.createPersonBtn.disabled =
    true;


  showCreatePersonStatus(
    "",
    "Création du contact…"
  );


  try {

    const token =
      await getAccessToken();


    /*
     * TODO:
     *
     * Replace CREATE_PERSON_ENDPOINT
     * with your real endpoint.
     *
     * Payload:
     *
     * {
     *   email,
     *   fullName,
     *   companyId
     * }
     */

    const res =
      await fetch(
        CREATE_PERSON_ENDPOINT,
        {

          method:
            "POST",

          headers: {

            "Content-Type":
              "application/json",

            Authorization:
              `Bearer ${token}`

          },

          body:
            JSON.stringify(
              payload
            )

        }
      );


    const text =
      await res.text();


    let data;


    try {

      data =
        JSON.parse(text);

    } catch {

      throw new Error(
        `Réponse invalide du serveur (statut ${res.status})`
      );

    }


    if (!res.ok) {

      throw new Error(
        data.errorMessage ||
        data.error ||
        `Le serveur a retourné le statut ${res.status}`
      );

    }


    showCreatePersonStatus(
      "success",
      "✔ Contact créé avec succès."
    );


    /*
     * Refresh the suggestions tree.
     *
     * The newly created person should now
     * be associated with the selected company.
     */

    await loadSuggestionsForSender();


  } catch (err) {

    console.error(
      "Create person failed:",
      err
    );


    showCreatePersonStatus(
      "error",
      "✖ " + err.message
    );


  } finally {

    isCreatingPerson =
      false;


    els.createPersonBtn.disabled =
      false;

  }

}


/* ================================================================== */
/* Company search                                                     */
/* ================================================================== */

function onCompanySearchInput() {

  const value =
    els.companySearchInput.value.trim();


  if (searchDebounceTimer) {

    clearTimeout(
      searchDebounceTimer
    );

  }


  /*
   * If the user starts a new search,
   * the previously selected company is no
   * longer considered selected.
   */

  setSelectedCompany(null);


  els.companyPeopleTree.innerHTML =
    "";

  els.companyPeopleTree.hidden =
    true;


  if (
    value.length <
    SEARCH_MIN_LENGTH
  ) {

    els.companyResults.innerHTML =
      "";


    els.companySearchSpinner.hidden =
      true;


    return;

  }


  searchDebounceTimer =
    setTimeout(
      () =>
        searchCompanies(value),
      SEARCH_DEBOUNCE_MS
    );

}


async function searchCompanies(value) {

  els.companySearchSpinner.hidden =
    false;


  els.companyResults.innerHTML =
    "";


  try {

    const token =
      await getAccessToken();


    const url =
      `${COMPANY_SEARCH_ENDPOINT}/` +
      encodeURIComponent(value);


    console.log(
      "Company search:",
      url
    );


    const result =
      await fetchJson(
        url,
        token
      );


    const companies =
      Array.isArray(result)
        ? result

        : Array.isArray(
            result.items
          )
          ? result.items

          : [];


    renderCompanyResults(
      companies
    );


  } catch (err) {

    console.error(
      "Company search failed:",
      err
    );


    renderCompanySearchError();


  } finally {

    els.companySearchSpinner.hidden =
      true;

  }

}


function renderCompanySearchError() {

  els.companyResults.innerHTML =
    "";


  const li =
    document.createElement("li");


  li.className =
    "company-results__empty";


  li.textContent =
    "Recherche impossible pour le moment.";


  els.companyResults.appendChild(
    li
  );

}


function renderCompanyResults(
  companies
) {

  els.companyResults.innerHTML =
    "";


  if (!companies.length) {

    const li =
      document.createElement("li");


    li.className =
      "company-results__empty";


    li.textContent =
      "Aucune entreprise trouvée.";


    els.companyResults.appendChild(
      li
    );


    return;

  }


  for (
    const company of companies
  ) {

    const li =
      document.createElement("li");


    li.className =
      "company-results__item";


    li.textContent =
      company.name ||
      `Entreprise #${company.id}`;


    li.addEventListener(
      "click",
      () =>
        onCompanySelected(
          company
        )
    );


    els.companyResults.appendChild(
      li
    );

  }

}


/* ================================================================== */
/* Company -> tree                                                    */
/* ================================================================== */

async function onCompanySelected(
  company
) {

  /*
   * Store the selected company immediately.
   *
   * From this point onward, this company is
   * the company associated with contact creation.
   */

  setSelectedCompany(
    company
  );


  els.companyResults.innerHTML =
    "";


  els.companySearchInput.value =
    company.name || "";


  els.companySearchSpinner.hidden =
    false;


  try {

    const token =
      await getAccessToken();


    const url =
      `${COMPANY_TREE_ENDPOINT}/${company.id}` +
      `/email-link-tree` +
      `?commercialProposalLimit=${
        COMMERCIAL_PROPOSAL_LIMIT
      }`;


    console.log(
      "Loading company tree:",
      url
    );


    const tree =
      await fetchJson(
        url,
        token
      );


    console.log(
      "Company tree response:",
      tree
    );


    const normalized =
      normalizeTree(tree);


    if (!normalized) {

      showCompanyTreeError();

      /*
       * Keep selected company and create form.
       *
       * The user can still create a person
       * for the company even if its tree
       * could not be loaded.
       */

      setSelectedCompany(
        company
      );

      return;
    }


    normalized.company =
      normalized.company || {

        id:
          company.id,

        type:
          "company",

        name:
          company.name,

        selectable:
          false

      };


    /*
     * Make sure selectedCompany contains
     * the normalized company information.
     */

    setSelectedCompany({

      id:
        normalized.company.id,

      name:
        normalized.company.name ||
        company.name

    });


    const hasItems =
      normalized.offers.length > 0 ||
      normalized.commercialProposals.length > 0;


    /*
     * Keep the user on the Search tab.
     *
     * This is now a complete company-selection
     * and contact-creation workflow.
     */

    switchTab(
      "search"
    );


    renderTree(
      normalized
    );


    if (!hasItems) {

      /*
       * There is no commercial content,
       * but this does NOT prevent contact creation.
       */

      showCompanyTreeEmpty(
        EMPTY_STATE_NO_COMPANY_ITEMS
      );

    }


  } catch (err) {

    console.error(
      "Failed to load company's fiche tree:",
      err
    );


    showCompanyTreeError();


    /*
     * Important:
     *
     * Do not clear selectedCompany.
     *
     * The company itself was selected successfully,
     * therefore the user should still be able to
     * create the person for that company.
     */

    setSelectedCompany(
      company
    );


    switchTab(
      "search"
    );


  } finally {

    els.companySearchSpinner.hidden =
      true;

  }

}


/* ================================================================== */
/* Company search tree states                                         */
/* ================================================================== */

function showCompanyTreeEmpty(
  message
) {

  /*
   * The create-person form remains visible.
   *
   * We only communicate that the company has
   * no commercial content.
   */

  const existing =
    els.companyPeopleTree.querySelector(
      ".company-tree-empty"
    );


  if (existing) {
    existing.remove();
  }


  const empty =
    document.createElement("div");


  empty.className =
    "state state--empty company-tree-empty";


  empty.innerHTML =
    `
      <div class="state__icon">⌕</div>
      <div>
        <strong>Aucune fiche commerciale</strong>
        <span>${message}</span>
      </div>
    `;


  els.companyPeopleTree.appendChild(
    empty
  );


  els.companyPeopleTree.hidden =
    false;

}


function showCompanyTreeError() {

  els.companyPeopleTree.innerHTML =
    "";


  const error =
    document.createElement("div");


  error.className =
    "state state--error";


  error.textContent =
    "Impossible de charger les fiches de cette entreprise.";


  els.companyPeopleTree.appendChild(
    error
  );


  els.companyPeopleTree.hidden =
    false;

}


/* ================================================================== */
/* Selection                                                          */
/* ================================================================== */

function selectTarget(
  type,
  id,
  label,
  cardEl
) {

  els.targetType.value =
    type;


  els.targetId.value =
    String(id);


  document
    .querySelectorAll(
      ".card-item--selected"
    )
    .forEach(
      (el) => {

        el.classList.remove(
          "card-item--selected"
        );

      }
    );


  if (cardEl) {

    cardEl.classList.add(
      "card-item--selected"
    );

  }


  showSelectedTarget(
    `${targetTypeLabel(type)} — ${label}`
  );

}


function targetTypeLabel(type) {

  switch (type) {

    case "offer":

      return "Offre";


    case "commercial_proposal":

      return "Proposition commerciale";

    default:

      return type;

  }

}


function syncSelectedTargetFromManualFields() {

  const id =
    els.targetId.value.trim();


  if (!id) {

    els.selectedTarget.hidden =
      true;

    return;

  }


  document
    .querySelectorAll(
      ".card-item--selected"
    )
    .forEach(
      (el) => {

        el.classList.remove(
          "card-item--selected"
        );

      }
    );


  const type =
    els.targetType.value;


  showSelectedTarget(
    `${targetTypeLabel(type)} #${id} (saisi manuellement)`
  );

}


function showSelectedTarget(text) {

  els.selectedTargetValue.textContent =
    text;


  els.selectedTarget.hidden =
    false;

}


function clearSelectedTarget() {

  document
    .querySelectorAll(
      ".card-item--selected"
    )
    .forEach(
      (el) => {

        el.classList.remove(
          "card-item--selected"
        );

      }
    );


  els.targetId.value =
    "";


  els.targetType.selectedIndex =
    0;


  els.selectedTargetValue.textContent =
    "";


  els.selectedTarget.hidden =
    true;

}


/* ================================================================== */
/* Backend linking                                                    */
/* ================================================================== */

function toAddinAddress(
  recipient
) {

  return {

    address:
      recipient.emailAddress,

    name:
      recipient.displayName || null

  };

}


async function onConfirm(
  targetType,
  targetIdRaw
) {

  if (isSubmitting) {
    return;
  }


  isSubmitting =
    true;


  els.confirmBtn.disabled =
    true;


  try {

    if (!targetIdRaw) {

      showStatus(
        "error",
        "✖ Veuillez sélectionner ou saisir un identifiant."
      );

      return;

    }


    const targetId =
      Number.parseInt(
        targetIdRaw,
        10
      );


    if (
      !Number.isInteger(targetId)
    ) {

      showStatus(
        "error",
        "✖ L’identifiant doit être un nombre."
      );

      return;

    }


    const restId =
      Office.context.mailbox.convertToRestId(
        currentItem.itemId,
        Office.MailboxEnums.RestVersion.v2_0
      );


    const linkTarget = {

      type:
        targetType,

      actionId:
        targetType === "action"
          ? targetId
          : null,

      offerId:
        targetType === "offer"
          ? targetId
          : null,

      commercialProposalId:
        targetType ===
        "commercial_proposal"
          ? targetId
          : null,

      companyId:
        null

    };
   const sharedMailbox = await getSharedMailbox();

    const payload = {

      itemId:
        restId,

      conversationId:
        currentItem.conversationId ||
        null,
    
      mailBox: sharedMailbox ? sharedMailbox.owner : null,

      from:
        currentItem.from
          ? toAddinAddress(
              currentItem.from
            )
          : null,

      to:
        (currentItem.to || [])
          .map(toAddinAddress),

      cc:
        (currentItem.cc || [])
          .map(toAddinAddress),

      receivedDateTime:
        currentItem.dateTimeCreated
          ? currentItem.dateTimeCreated.toISOString()
          : null,

      linkTargets: [
        linkTarget
      ]

    };


    await callBackend(
      payload,
      targetType,
      targetId
    );


  } finally {

    isSubmitting =
      false;


    els.confirmBtn.disabled =
      false;

  }

}


/* ================================================================== */
/* Authentication                                                     */
/* ================================================================== */

async function getAccessToken() {

  const now =
    Date.now();


  if (
    cachedToken &&
    now <
      cachedTokenExpiry -
      TOKEN_REFRESH_BUFFER_MS
  ) {

    return cachedToken;

  }


  if (tokenPromise) {
    return tokenPromise;
  }


  tokenPromise =
    Office.auth
      .getAccessToken({

        allowSignInPrompt:
          true,

        allowConsentPrompt:
          true

      })

      .then(
        (token) => {

          cachedToken =
            token;


          cachedTokenExpiry =
            decodeTokenExpiry(
              token
            );


          return token;

        }
      )

      .finally(
        () => {

          tokenPromise =
            null;

        }
      );


  return tokenPromise;

}


function invalidateAccessToken() {

  cachedToken =
    null;

  cachedTokenExpiry =
    0;

}


function decodeTokenExpiry(
  token
) {

  try {

    const payload =
      token.split(".")[1];


    const normalized =
      payload
        .replace(
          /-/g,
          "+"
        )
        .replace(
          /_/g,
          "/"
        );


    const decoded =
      JSON.parse(
        atob(normalized)
      );


    if (decoded.exp) {

      return decoded.exp * 1000;

    }


  } catch (err) {

    console.warn(
      "Could not decode token expiry, falling back:",
      err
    );

  }


  return (
    Date.now() +
    60 * 60 * 1000
  );

}


/* ================================================================== */
/* HTTP                                                               */
/* ================================================================== */

async function fetchJson(
  url,
  token,
  allowRetry = true
) {

  const res =
    await fetch(
      url,
      {

        headers: {

          Authorization:
            `Bearer ${token}`

        }

      }
    );


  if (
    res.status === 401 &&
    allowRetry
  ) {

    invalidateAccessToken();


    const freshToken =
      await getAccessToken();


    return fetchJson(
      url,
      freshToken,
      false
    );

  }


  const text =
    await res.text();


  let data;


  try {

    data =
      JSON.parse(text);

  } catch {

    throw new Error(
      `Réponse invalide du serveur (statut ${res.status})`
    );

  }


  if (!res.ok) {

    throw new Error(
      data.error ||
      `Le serveur a retourné le statut ${res.status}`
    );

  }


  return data;

}


/* ================================================================== */
/* Status                                                             */
/* ================================================================== */

function showStatus(
  kind,
  message
) {

  els.status.className =
    "status" +
    (
      kind
        ? " " + kind
        : ""
    );


  els.status.textContent =
    message;

}


/* ================================================================== */
/* Backend call                                                       */
/* ================================================================== */

async function callBackend(
  payload,
  targetType,
  targetId,
  allowRetry = true
) {

  showStatus(
    "",
    "Enregistrement…"
  );


  try {

    const token =
      await getAccessToken();


    const res =
      await fetch(
        LINK_ENDPOINT,
        {

          method:
            "POST",

          headers: {

            "Content-Type":
              "application/json",

            Authorization:
              `Bearer ${token}`

          },

          body:
            JSON.stringify(
              payload
            )

        }
      );


    if (
      res.status === 401 &&
      allowRetry
    ) {

      invalidateAccessToken();


      return callBackend(
        payload,
        targetType,
        targetId,
        false
      );

    }


    const text =
      await res.text();


    let data;


    try {

      data =
        JSON.parse(text);

    } catch {

      throw new Error(
        `Réponse invalide du serveur (statut ${res.status}) : ` +
        text.slice(0, 300)
      );

    }


    if (!res.ok) {

      throw new Error(
        data.errorMessage ||
        data.error ||
        `Le serveur a retourné le statut ${res.status}`
      );

    }


    switch (data.status) {

      case "linked":

        showStatus(
          "success",
          `✔ L’e-mail a été lié à ` +
          `${targetTypeLabel(targetType).toLowerCase()} ${targetId}.`
        );

        break;


      case "archived_new_link":

        showStatus(
          "success",
          `✔ L’e-mail était déjà archivé et a été lié à ` +
          `${targetTypeLabel(targetType).toLowerCase()} ${targetId}.`
        );

        break;


      case "already_linked":

        showStatus(
          "success",
          `ℹ️ Cet e-mail est déjà lié à ` +
          `${targetTypeLabel(targetType).toLowerCase()} ${targetId}.`
        );

        break;


      case "more_recent_version":

        showStatus(
          "error",
          "⚠️ Une version plus récente de cette conversation est disponible. " +
          "Veuillez actualiser la conversation avant de continuer."
        );

        break;


      case "failed":

        showStatus(
          "error",
          data.errorMessage ||
          "✖ Une erreur est survenue lors de l’importation de l’e-mail."
        );

        break;


      default:

        showStatus(
          "success",
          `✔ Opération effectuée (${data.status}).`
        );

        break;

    }


  } catch (err) {

    console.error(
      "Link email failed:",
      err
    );


    showStatus(
      "error",
      "✖ " + err.message
    );

  }

}

function getSharedMailbox() {
  return new Promise((resolve) => {
    const item = Office.context.mailbox.item;

    // API not available on this client/version
    if (!item || typeof item.getSharedPropertiesAsync !== "function") {
      resolve(null);
      return;
    }

    item.getSharedPropertiesAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve({
          owner: result.value.owner,               // address of the shared mailbox/owner
          targetMailbox: result.value.targetMailbox
        });
      } else {
        resolve(null); // not shared: the email is in the user's own mailbox
      }
    });
  });
}
